/**
 * CreatorTabStrip.tsx — per-creator tab strip under the header (Design Spec §06).
 *
 * One tab per assigned creator (D-05-01). An optional "All" tab (`showAllTab`), selected via
 * the sentinel creatorId `'all'`, prepends the strip for admin all-accounts mode (Phase
 * 12-03, D-04) AND now for chatters too (Phase 13, D-13-01 — additive, supersedes the old
 * "No 'All' tab for chatters" D-05-02): the tab is opt-in and NOT the default landing for
 * chatters (D-13-02 — a fresh chatter login still lands on the first-creator tab). Ordered
 * most-recent-activity first by the server (D-05-04). Each creator tab carries:
 *   - creator name (truncated)
 *   - unread bubble (sum of accounts' unread; hidden when 0 — D-05-03)
 *   - worst-of health pill (hidden when all online — D-05-03)
 *
 * The strip is horizontally scrollable (`overflow-x-auto`) so it stays usable with
 * tens of creators (D-04). Switching a tab navigates to /inbox/:creatorId (or /inbox
 * for 'all'), clearing any open thread, which belongs to a different creator/tab.
 */

import * as React from 'react';
import { AccountHealthBadge } from './AccountHealthBadge.js';
import { computeWorstHealth, type HealthMap } from '../lib/health.js';
import { arrowDirection, focusRovingSibling } from '../lib/keyboard.js';
import type { CreatorRollupDto } from '../lib/api-client.js';

interface CreatorTabStripProps {
  creators: CreatorRollupDto[];
  /** The active creatorId, or the sentinel `'all'` when the merged "All" tab is active. */
  activeCreatorId: string | null;
  healthMap: HealthMap;
  onSelect: (creatorId: string) => void;
  /**
   * Batch transactions unread-counts map (D-07-15): {creatorId: count} for ALL assigned
   * creators. Drives a tx notification badge on EVERY tab, including inactive ones.
   * Server-derived (GET /chatter/transactions/unread-counts) — client cannot widen scope.
   */
  txUnreadMap?: Record<string, number>;
  /**
   * Admin all-accounts mode (Phase 12-03, D-04): prepend an "All" tab (the merged
   * cross-account list) as the first, default-selected tab. Omitted/false in chatter
   * mode — no behavior change there.
   */
  showAllTab?: boolean;
}

export function CreatorTabStrip({
  creators,
  activeCreatorId,
  healthMap,
  onSelect,
  txUnreadMap,
  showAllTab,
}: CreatorTabStripProps) {
  return (
    <div
      role="tablist"
      aria-label="Creators"
      className="flex items-stretch gap-0.5 overflow-x-auto border-b border-border bg-bg-card px-2 shrink-0"
    >
      {showAllTab && (
        <AllTab isActive={activeCreatorId === 'all'} onSelect={onSelect} />
      )}
      {creators.map((creator) => (
        <CreatorTab
          key={creator.creatorId}
          creator={creator}
          isActive={creator.creatorId === activeCreatorId}
          healthMap={healthMap}
          onSelect={onSelect}
          txUnread={txUnreadMap?.[creator.creatorId] ?? 0}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// "All" tab — admin all-accounts mode (Phase 12-03, D-04)
// ---------------------------------------------------------------------------

function AllTab({
  isActive,
  onSelect,
}: {
  isActive: boolean;
  onSelect: (creatorId: string) => void;
}) {
  return (
    <button
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      onClick={() => onSelect('all')}
      onKeyDown={(e) => {
        const dir = arrowDirection(e.key);
        if (dir) {
          e.preventDefault();
          focusRovingSibling(e.currentTarget.parentElement, e.currentTarget, dir);
        }
      }}
      title="All creators"
      className={[
        'inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition-colors',
        'border-b-2 -mb-px shrink-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:rounded',
        isActive
          ? 'text-accent border-accent'
          : 'text-text-muted border-transparent hover:text-text',
      ].join(' ')}
    >
      All
    </button>
  );
}

interface CreatorTabProps {
  creator: CreatorRollupDto;
  isActive: boolean;
  healthMap: HealthMap;
  onSelect: (creatorId: string) => void;
  /** Transaction unread count for this creator from the batch map (D-07-15). */
  txUnread: number;
}

function CreatorTab({ creator, isActive, healthMap, onSelect, txUnread }: CreatorTabProps) {
  const worstHealth = computeWorstHealth(creator.accounts, healthMap, creator.tabHealth);
  const showHealthPill = worstHealth !== 'online';
  const unread = creator.tabUnread;

  return (
    <button
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      onClick={() => onSelect(creator.creatorId)}
      onKeyDown={(e) => {
        const dir = arrowDirection(e.key);
        if (dir) {
          e.preventDefault();
          focusRovingSibling(e.currentTarget.parentElement, e.currentTarget, dir);
        }
      }}
      title={creator.creatorName}
      className={[
        'inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition-colors',
        'border-b-2 -mb-px shrink-0 max-w-[180px]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:rounded',
        isActive
          ? 'text-accent border-accent'
          : 'text-text-muted border-transparent hover:text-text',
      ].join(' ')}
    >
      <span className="truncate">{creator.creatorName}</span>

      {unread > 0 && (
        <span
          className={[
            'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold leading-none shrink-0',
            isActive ? 'bg-accent text-white' : 'bg-bg-hover text-text-muted',
          ].join(' ')}
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}

      {/* Transactions unread badge — shows on EVERY assigned creator tab (D-07-15).
          Driven by the batch txUnreadMap from ChatterWorkspace; inactive tabs badge too. */}
      {txUnread > 0 && (
        <span
          className={[
            'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full',
            'text-[11px] font-bold leading-none shrink-0',
            isActive ? 'bg-amber-500 text-amber-50' : 'bg-amber-500/20 text-amber-600 dark:text-amber-400',
          ].join(' ')}
          title={`${txUnread} new notification${txUnread !== 1 ? 's' : ''}`}
          aria-label={`${txUnread} new notification${txUnread !== 1 ? 's' : ''}`}
        >
          {txUnread > 99 ? '99+' : txUnread}
        </span>
      )}

      {showHealthPill && (
        <span className="shrink-0">
          <AccountHealthBadge state={worstHealth} compact />
        </span>
      )}
    </button>
  );
}
